import styled, { keyframes, css } from "styled-components";
import { tokens, surface, hairline } from "../../styles/designTokens";

const meshDrift = keyframes`
  0%   { transform: translate(0, 0) scale(1); }
  50%  { transform: translate(-20px, 18px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
`;
const pulseDot = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.9; }
  50%      { transform: scale(1.15); opacity: 0.5; }
`;
const fadeInOnly = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;
const skeletonPulse = keyframes`
  0%, 100% { opacity: 0.6; }
  50%      { opacity: 0.3; }
`;

const mono = css`
  font-family: ${tokens.fontMono};
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
`;
const eyebrow = css`
  ${mono};
  font-size: ${tokens.fsEyebrow};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.text};
  opacity: 0.45;
`;
const cardBase = (theme) => css`
  background: ${surface(theme, 0)};
  border: 1px solid ${hairline(theme)};
  border-radius: ${tokens.radiusLg};
  transition: border-color ${tokens.durBase} ${tokens.easeOut},
              background ${tokens.durBase} ${tokens.easeOut};
`;

export const Container = styled.div`
  width: 100%;
  max-width: 90em;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 2em 2em 2em;
  position: relative;
  min-height: 100vh;
  overflow-y: auto;
  animation: ${fadeInOnly} ${tokens.durSlow} ${tokens.easeOut};

  &::before {
    content: "";
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(800px 600px at 15% 10%, ${tokens.brandSoft} 0%, transparent 60%),
      radial-gradient(700px 500px at 85% 70%, rgba(6, 182, 212, 0.05) 0%, transparent 65%);
    animation: ${meshDrift} 24s ${tokens.easeInOut} infinite;
    mask-image: radial-gradient(ellipse at center, black 40%, transparent 90%);
  }

  > * { position: relative; z-index: 1; }

  /* ========== HEADER ========== */
  .header-info {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 24px 0 8px 0;
    margin-bottom: 16px;
    flex-shrink: 0;

    .header-details {
      flex: 1;
      min-width: 0;

      h3 {
        margin: 4px 0 8px 0;
        font-size: 1.7em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        letter-spacing: -0.025em;
        line-height: 1.15;
      }

      h3::before {
        content: "CLIENTES · DASHBOARD";
        display: block;
        ${eyebrow};
        margin-bottom: 6px;
        opacity: 0.4;
      }

      .header-meta {
        display: flex;
        gap: 10px;
        align-items: center;
        font-size: 0.78em;

        .status-tag {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          ${mono};
          font-size: 0.95em;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: ${tokens.brand};

          &::before {
            content: "";
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: ${tokens.brand};
            box-shadow: 0 0 8px ${tokens.brandRing};
            animation: ${pulseDot} 2.2s ${tokens.easeInOut} infinite;
          }

          &.error {
            color: ${tokens.danger};
            &::before { background: ${tokens.danger}; box-shadow: 0 0 8px rgba(239,68,68,0.28); }
          }
        }

        .update-tag {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          ${mono};
          font-size: 0.9em;
          &::before { content: "· "; opacity: 0.5; }
        }

        .refresh-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 5px;
          background: transparent;
          border: none;
          border-radius: ${tokens.radiusSm};
          cursor: pointer;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          transition: all ${tokens.durBase} ${tokens.easeOut};

          &:hover { opacity: 1; background: ${({ theme }) => surface(theme, 1)}; }
          &:disabled { opacity: 0.25; cursor: not-allowed; }
          svg { font-size: 0.85em; }
        }
      }
    }
  }

  .spin { animation: ${spin} 1s linear infinite; }
  .skeleton-loading { animation: ${skeletonPulse} 1.5s ease-in-out infinite; opacity: 0.6; }

  /* ========== TABS HEADER ========== */
  .tabs-header {
    display: flex;
    gap: 8px;
    margin-bottom: 14px;
    flex-shrink: 0;

    .tab {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 7px 14px;
      background: transparent;
      border: 1px solid transparent;
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.55;
      font-size: 0.78em;
      font-weight: 500;
      cursor: pointer;
      transition: all ${tokens.durBase} ${tokens.easeOut};
      ${mono};
      text-transform: uppercase;
      letter-spacing: 0.04em;

      &:hover { opacity: 0.85; background: ${({ theme }) => surface(theme, 1)}; }

      &.active {
        opacity: 1;
        background: ${tokens.brandSoft};
        border-color: ${tokens.brandRing};
        color: ${tokens.brand};
      }

      svg { font-size: 0.95em; }
    }
  }

  /* ========== KPI CARDS ========== */
  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;

    &.main-stats { margin-bottom: 12px; }
    &.tax-stats { grid-template-columns: repeat(6, 1fr); }
    &.secondary-stats { grid-template-columns: repeat(5, 1fr); }

    @media (max-width: 1200px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 500px)  { grid-template-columns: 1fr; }
  }

  .kpi-card {
    ${({ theme }) => cardBase(theme)};
    padding: 16px 18px 14px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 96px;
    position: relative;
    overflow: hidden;

    &:hover {
      border-color: ${({ theme }) => hairline(theme, true)};
      background: ${({ theme }) => surface(theme, 1)};
    }

    .card-content {
      display: flex;
      flex-direction: column;
      gap: 8px;

      .thumb {
        opacity: 0.35;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        display: inline-flex;
        align-items: center;
      }

      .text {
        display: flex;
        flex-direction: column;
        gap: 2px;

        p { margin: 0; }

        p:first-child {
          ${mono};
          font-size: 1.6em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        p:last-child {
          font-size: 0.72em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          margin-top: 2px;
          letter-spacing: -0.005em;
        }
      }
    }

    &.success .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.warning .card-content .thumb { color: ${tokens.info};  opacity: 0.65; }
    &.danger  .card-content .thumb { color: ${tokens.danger}; opacity: 0.65; }
    &.info    .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }
    &.primary .card-content .thumb { color: ${tokens.brand}; opacity: 0.65; }
    &.cte     .card-content .thumb { color: ${tokens.info};  opacity: 0.65; }
    &.nfe     .card-content .thumb { color: ${tokens.warn};  opacity: 0.65; }

    &.compact {
      min-height: 72px;
      padding: 12px 14px;
      .text p:first-child { font-size: 1.35em; }
      .text p:last-child { font-size: 0.68em; }
    }
  }

  /* ========== FILTERS / TOOLBAR ========== */
  .filters {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    margin-bottom: 14px;
    flex-shrink: 0;
    flex-wrap: wrap;

    .search-input-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 7px 12px;
      ${({ theme }) => cardBase(theme)};
      border-radius: ${tokens.radiusMd};
      flex: 1;
      max-width: 360px;
      transition: border-color ${tokens.durBase} ${tokens.easeOut};

      &:focus-within { border-color: ${tokens.brandRing}; }

      .search-icon {
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 0.9em;
        flex-shrink: 0;
      }

      .search-input {
        background: transparent;
        border: none;
        outline: none;
        color: ${({ theme }) => theme.text};
        font-family: ${tokens.fontSans};
        font-size: 0.82em;
        flex: 1;
        letter-spacing: -0.005em;

        &::placeholder { opacity: 0.4; }
      }
    }

    @media (max-width: 900px) {
      .search-input-wrapper { max-width: none; }
    }
  }


  /* ========== STATS TAB ========== */
  .stats-tab {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px 0;
    width: 100%;
    align-self: stretch;

    .stats-placeholder {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px 20px;
      background: ${({ theme }) => theme.card};
      border-radius: 12px;
      border: 1px solid ${({ theme }) => theme.border};

      .stats-icon {
        font-size: 48px;
        color: ${({ theme }) => theme.primary};
        opacity: 0.5;
        margin-bottom: 16px;
      }

      h4 {
        font-size: 18px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        margin: 0 0 8px 0;
      }

      p {
        font-size: 14px;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        margin: 0;
      }
    }
  }

  /* ========== SEGMENTS CARD ========== */
  .segments-card {
    width: 100%;
    height: 100%;
    border-radius: ${tokens.radiusLg};
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => surface(theme, 0)};
    overflow: hidden;
    padding: 10px;

    .donut-wrapper {
      display: flex;
      flex-direction: column;
      gap: 14px;
      align-items: center;
      height: 100%;

      .donut-header {
        width: 100%;
        text-align: left;
        margin-bottom: 4px;

        h4 {
          margin: 0 0 3px 0;
          font-size: 0.82em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          letter-spacing: -0.01em;
        }

        p {
          margin: 0;
          font-size: 0.68em;
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
          font-weight: 400;
        }
      }

      .donut-chart {
        width: 180px;
        height: 180px;
        flex-shrink: 0;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        .donut-center {
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);

          .donut-total {
            font-size: 1.5em;
            font-weight: 700;
            color: #fff;
            letter-spacing: -0.02em;
            line-height: 1;
          }

          .donut-label {
            font-size: 0.6em;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            margin-top: 3px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
          }
        }
      }

      .segments-list {
        width: 100%;
        flex: 1;
        min-height: 0;
        max-height: 340px;
        overflow-y: auto;
        padding-right: 6px;
        display: flex;
        flex-direction: column;
        gap: 8px;

        &::-webkit-scrollbar { width: 3px; }
        &::-webkit-scrollbar-thumb {
          background: ${({ theme }) => hairline(theme, true)};
          border-radius: 2px;
        }

        .segment-item {
          padding: 7px 10px;
          background: ${({ theme }) => surface(theme, 1)};
          border-radius: ${tokens.radiusSm};
          border: 1px solid ${({ theme }) => hairline(theme)};
          transition: border-color ${tokens.durBase} ${tokens.easeOut},
                      opacity ${tokens.durBase} ${tokens.easeOut};
          cursor: pointer;
          opacity: 0.7;

          &:hover {
            border-color: ${({ theme }) => hairline(theme, true)};
            opacity: 1;
          }

          .segment-header {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 5px;

            .segment-color {
              width: 6px;
              height: 6px;
              border-radius: 2px;
              flex-shrink: 0;
            }

            .segment-name {
              flex: 1;
              font-size: 0.72em;
              font-weight: 500;
              color: ${({ theme }) => theme.text};
              letter-spacing: -0.005em;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }

            .segment-count {
              font-size: 0.65em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              ${mono};
              opacity: 0.6;
            }
          }

          .segment-bar {
            width: 100%;
            height: 3px;
            background: ${({ theme }) => hairline(theme)};
            border-radius: 2px;
            overflow: hidden;
            margin-bottom: 4px;
            box-sizing: border-box;

            .segment-progress {
              height: 100%;
              border-radius: 2px;
              transition: width 0.3s ease;
            }
          }

          .segment-value {
            font-size: 0.68em;
            ${mono};
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            text-align: right;
          }
        }
      }
    }
  }

  /* ========== MONTHLY CHART ========== */
  .monthly-chart {
    width: 100%;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    overflow: hidden;
    margin-top: 24px;

    .chart-header {
      padding: 16px 20px;
      display: flex;
      align-items: center;

      svg {
        margin-right: 8px;
        opacity: 0.7;
        color: ${({ theme }) => theme.text};
        font-size: 16px;
      }

      h4 {
        margin: 0;
        font-size: 14px;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
      }
    }

    .chart-content {
      display: grid;
      grid-template-columns: 280px 1fr;
      gap: 0;
      min-height: 320px;

      @media (max-width: 900px) {
        grid-template-columns: 1fr;
      }
    }

    .month-info-card {
      border-right: 1px solid ${({ theme }) => theme.border};
      padding: 20px;

      .month-info-header {
        margin-bottom: 20px;

        h5 {
          margin: 0 0 4px 0;
          font-size: 18px;
          font-weight: 700;
          color: ${({ theme }) => theme.text};
        }

        .month-index {
          font-size: 12px;
          color: ${({ theme }) => theme.text};
          opacity: 0.6;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }

      .month-metrics {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .metric-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          background: ${({ theme }) => theme.card};
          border: 1px solid ${({ theme }) => theme.border};
          border-radius: 6px;

          &.novos {
            border-left: 4px solid #3b82f6;
          }
          &.ativos {
            border-left: 4px solid #10b981;
          }
          &.faturamento {
            border-left: 4px solid #f59e0b;
          }

          .metric-icon {
            font-size: 16px;
            margin-top: 2px;
            opacity: 0.7;
          }

          .metric-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 2px;

            .metric-label {
              font-size: 11px;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            .metric-value {
              font-size: 16px;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
              font-family: monospace;
            }
          }
        }
      }
    }

    .chart-container {
      padding: 20px;

      * {
        outline: none !important;
      }

      *:focus {
        outline: none !important;
      }

      .chart-header-bar {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 16px;

        h4 {
          margin: 0;
          font-size: 0.95em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }

        .chart-legend {
          display: flex;
          gap: 16px;
          margin-bottom: 0;

          .legend-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 11px;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;

            .legend-color {
              width: 10px;
              height: 10px;
              border-radius: 2px;
            }
          }
        }
      }

      .chart-placeholder {
        height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: ${({ theme }) => theme.contrast};
        border-radius: 12px;
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 14px;
      }

      /* Transição suave nas barras do gráfico */
      .recharts-bar-rectangle {
        transition: opacity 0.2s ease;
      }

      /* Remover borda do gráfico */
      .recharts-wrapper,
      .recharts-wrapper *,
      .recharts-surface,
      .recharts-responsive-container {
        outline: none !important;
        border: none !important;
      }

      .recharts-wrapper:focus,
      .recharts-surface:focus {
        outline: none !important;
      }

      .recharts-cartesian-grid-bg {
        fill: transparent !important;
        stroke: transparent !important;
      }

      .recharts-cartesian-grid rect {
        stroke: transparent !important;
      }

      /* Tooltip Customizado */
      .recharts-tooltip-wrapper {
        outline: none !important;
        border: none !important;
      }

      .custom-tooltip {
        background: ${({ theme }) => theme.card};
        border: none;
        outline: none;
        border-radius: 12px;
        padding: 16px;
        min-width: 200px;
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);

        .tooltip-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid ${({ theme }) => theme.border};

          .tooltip-month {
            font-size: 1em;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
          }

          .tooltip-index {
            font-size: 0.7em;
            font-weight: 600;
            color: ${({ theme }) => theme.primary};
            background: rgba(16, 185, 129, 0.1);
            padding: 3px 8px;
            border-radius: 6px;
          }
        }

        .tooltip-content {
          display: flex;
          flex-direction: column;
          gap: 10px;

          .tooltip-item {
            display: flex;
            align-items: center;
            gap: 8px;

            .tooltip-dot {
              width: 10px;
              height: 10px;
              border-radius: 3px;
              flex-shrink: 0;
            }

            .tooltip-label {
              flex: 1;
              font-size: 0.8em;
              color: ${({ theme }) => theme.text};
              opacity: 0.7;
            }

            .tooltip-value {
              font-size: 0.85em;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
              font-family: "Inter", monospace;
            }
          }
        }

        .tooltip-footer {
          margin-top: 12px;
          padding-top: 10px;
          border-top: 1px dashed ${({ theme }) => theme.border};
          text-align: center;

          span {
            font-size: 0.7em;
            color: ${({ theme }) => theme.primary};
            font-weight: 500;
            opacity: 0.8;
          }
        }
      }
    }
  }

  /* ========== STATS TAB ========== */
  .stats-tab {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 20px 0;
    width: 100%;

    .charts-container {
      display: flex;
      flex-direction: column;
      gap: 24px;
      width: 100%;
    }

    .charts-row {
      display: flex;
      justify-content: space-between;
      gap: 20px;
      width: 100%;

      .stats-section.chart-card:first-child {
        flex: 0 0 35%;
      }

      .stats-section.chart-card:last-child {
        flex: 1;
      }
    }

    .stats-section {
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      padding: 20px;

      &.chart-card {
        min-height: auto;
      }

      .section-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 16px;
        padding-bottom: 10px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        svg {
          font-size: 1.2em;
          color: ${({ theme }) => theme.primary};
          opacity: 0.8;
        }

        h4 {
          margin: 0;
          font-size: 0.95em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }

      .donut-wrapper {
        display: flex;
        flex-direction: row;
        gap: 20px;
        align-items: center;
        justify-content: flex-start;

        .donut-chart {
          position: relative;
          display: flex;
          justify-content: center;
          align-items: center;
          flex-shrink: 0;

          svg {
            filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1));
          }

          .donut-center {
            position: absolute;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;

            .donut-total {
              font-size: 1.6em;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
            }

            .donut-label {
              font-size: 0.7em;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
            }
          }
        }

        .categories-list {
          display: flex;
          flex-direction: column;
          gap: 6px;
          flex: 1;

          .category-item {
            .category-header {
              display: flex;
              align-items: center;
              gap: 8px;

              .category-color {
                width: 10px;
                height: 10px;
                border-radius: 3px;
                flex-shrink: 0;
              }

              .category-name {
                flex: 1;
                font-size: 0.78em;
                font-weight: 500;
                color: ${({ theme }) => theme.text};
              }

              .category-count {
                font-size: 0.72em;
                font-weight: 600;
                color: ${({ theme }) => theme.text};
                background: ${({ theme }) => theme.buttonBg};
                padding: 2px 8px;
                border-radius: 6px;
                min-width: 24px;
                text-align: center;
              }
            }
          }
        }
      }

      .bar-chart-wrapper {
        .chart-legend {
          display: flex;
          gap: 16px;
          margin-bottom: 16px;

          .legend-item {
            display: flex;
            align-items: center;
            gap: 6px;

            .legend-color {
              width: 10px;
              height: 10px;
              border-radius: 2px;
            }

            span {
              font-size: 0.75em;
              color: ${({ theme }) => theme.text};
              opacity: 0.7;
            }
          }
        }

        svg {
          width: 100%;
          height: auto;
        }
      }
    }

    /* Rankings Row */
    .rankings-row {
      display: flex;
      justify-content: space-between;
      gap: 24px;
      width: 100%;
    }

    .ranking-card {
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      padding: 20px;
      display: flex;
      flex-direction: column;
      flex: 1;
      height: 380px;

      .ranking-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        svg {
          font-size: 1.1em;
          color: ${({ theme }) => theme.primary};
          opacity: 0.8;
        }

        h4 {
          margin: 0;
          font-size: 0.9em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }

      .ranking-list {
        flex: 1;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 8px;

        &::-webkit-scrollbar {
          width: 4px;
        }

        &::-webkit-scrollbar-thumb {
          background: ${({ theme }) => theme.border};
          border-radius: 2px;
        }

        &.tipo-pessoa {
          gap: 12px;

          .tipo-card {
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 16px;
            border-radius: 12px;
            border: 1px solid ${({ theme }) => theme.border};

            &.pj {
              background: rgba(59, 130, 246, 0.05);
              border-color: rgba(59, 130, 246, 0.2);
            }

            &.pf {
              background: rgba(16, 185, 129, 0.05);
              border-color: rgba(16, 185, 129, 0.2);
            }

            .tipo-icon {
              width: 48px;
              height: 48px;
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 1.3em;

              .pj & {
                background: rgba(59, 130, 246, 0.15);
                color: #3b82f6;
              }

              .pf & {
                background: rgba(16, 185, 129, 0.15);
                color: #10b981;
              }
            }

            .tipo-info {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 2px;

              .tipo-label {
                font-size: 0.9em;
                font-weight: 600;
                color: ${({ theme }) => theme.text};
              }

              .tipo-count {
                font-size: 0.75em;
                color: ${({ theme }) => theme.text};
                opacity: 0.6;
              }

              .tipo-value {
                font-size: 0.85em;
                font-weight: 700;
                color: ${({ theme }) => theme.text};
                font-family: monospace;
              }
            }

            .tipo-percent {
              font-size: 1.4em;
              font-weight: 700;
              color: ${({ theme }) => theme.text};
              opacity: 0.8;
            }
          }
        }

        .ranking-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 8px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          border-bottom: 1px solid ${({ theme }) => theme.border};

          &:hover {
            background: rgba(255, 255, 255, 0.03);
          }

          &:last-child {
            border-bottom: none;
          }

          .ranking-position {
            width: 24px;
            height: 24px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.75em;
            font-weight: 700;
            background: ${({ theme }) => theme.buttonBg};
            color: ${({ theme }) => theme.text};
          }

          .ranking-thumb {
            width: 36px;
            height: 36px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            flex-shrink: 0;
            border: 1px solid rgba(255, 255, 255, 0.1);

            &.faturamento {
              background: rgba(16, 185, 129, 0.15);
              color: #10b981;
            }

            &.impostos {
              background: rgba(239, 68, 68, 0.15);
              color: #ef4444;
            }
          }

          .ranking-info {
            flex: 1;
            min-width: 0;

            .ranking-name {
              font-size: 0.8em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              display: block;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              margin-bottom: 4px;
            }

            .ranking-tags {
              display: flex;
              gap: 4px;
              flex-wrap: wrap;

              .category-tag,
              .code-tag {
                font-size: 0.65em;
                padding: 2px 5px;
                border-radius: 3px;
                font-weight: 500;
              }

              .category-tag {
                background: rgba(59, 130, 246, 0.15);
                color: #60a5fa;
                border: 1px solid rgba(59, 130, 246, 0.25);
              }

              .code-tag {
                background: rgba(156, 163, 175, 0.15);
                color: #9ca3af;
                border: 1px solid rgba(156, 163, 175, 0.25);
              }
            }
          }

          .ranking-value {
            font-size: 0.85em;
            font-weight: 700;
            font-family: monospace;
            flex-shrink: 0;

            &.faturamento {
              color: #10b981;
            }

            &.impostos {
              color: #ef4444;
            }
          }
        }
      }
    }

    /* KPIs Executivos */
    .executive-kpis-row {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 16px;

      .exec-kpi-card {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 20px;
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        transition: all 0.2s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        }

        .kpi-icon {
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          font-size: 1.3em;

          &.blue {
            background: rgba(59, 130, 246, 0.1);
            color: #3b82f6;
          }
          &.green {
            background: rgba(16, 185, 129, 0.1);
            color: #10b981;
          }
          &.orange {
            background: rgba(245, 158, 11, 0.1);
            color: #f59e0b;
          }
          &.red {
            background: rgba(239, 68, 68, 0.1);
            color: #ef4444;
          }
          &.purple {
            background: rgba(139, 92, 246, 0.1);
            color: #8b5cf6;
          }
        }

        .kpi-content {
          display: flex;
          flex-direction: column;
          gap: 2px;

          .kpi-value {
            font-size: 1.2em;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
          }

          .kpi-label {
            font-size: 0.75em;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
          }

          .kpi-change {
            font-size: 0.7em;
            font-weight: 600;

            &.positive {
              color: #10b981;
            }
            &.negative {
              color: #ef4444;
            }
          }
        }
      }
    }

    /* Gráficos Avançados */
    .advanced-charts-row {
      display: grid;
      grid-template-columns: 1.5fr 1fr;
      gap: 20px;

      .advanced-chart-card {
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 16px;
        padding: 20px;

        .chart-header-advanced {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;

          .chart-title-group {
            display: flex;
            align-items: center;
            gap: 10px;

            .chart-icon {
              font-size: 1.2em;
              color: ${({ theme }) => theme.primary};
            }

            h4 {
              font-size: 1em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              margin: 0;
            }
          }

          .chart-subtitle {
            font-size: 0.8em;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
          }

          .chart-period-selector {
            display: flex;
            gap: 4px;

            .period-btn {
              padding: 6px 12px;
              border: 1px solid ${({ theme }) => theme.border};
              border-radius: 6px;
              background: transparent;
              color: ${({ theme }) => theme.text};
              font-size: 0.75em;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;

              &:hover {
                background: ${({ theme }) => theme.buttonBg};
              }

              &.active {
                background: ${({ theme }) => theme.primary};
                border-color: ${({ theme }) => theme.primary};
                color: #fff;
              }
            }
          }
        }

        .chart-body {
          &.tax-composition {
            display: flex;
            align-items: center;
            gap: 24px;
            padding: 10px 0;

            .tax-donut-container {
              position: relative;
              flex-shrink: 0;

              .tax-center {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                display: flex;
                flex-direction: column;
                align-items: center;

                .tax-total {
                  font-size: 0.9em;
                  font-weight: 700;
                  color: ${({ theme }) => theme.text};
                }

                .tax-label {
                  font-size: 0.65em;
                  color: ${({ theme }) => theme.text};
                  opacity: 0.6;
                }
              }
            }

            .tax-legend {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 12px;

              .tax-item {
                display: flex;
                align-items: center;
                gap: 12px;

                .tax-color {
                  width: 12px;
                  height: 12px;
                  border-radius: 3px;
                }

                .tax-name {
                  flex: 1;
                  font-size: 0.85em;
                  color: ${({ theme }) => theme.text};
                }

                .tax-value {
                  font-size: 0.85em;
                  font-weight: 600;
                  color: ${({ theme }) => theme.text};
                  font-family: monospace;
                }
              }
            }
          }
        }
      }
    }

    /* Tabela de Performance */
    .performance-table-section {
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-radius: 16px;
      overflow: hidden;

      .section-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 20px 24px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        svg {
          font-size: 1.2em;
          color: ${({ theme }) => theme.primary};
        }

        h4 {
          margin: 0;
          font-size: 1em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }

      .performance-table {
        width: 100%;

        .table-header {
          display: grid;
          grid-template-columns: 1.5fr 0.8fr 1.2fr 1.2fr 1.2fr 1fr;
          gap: 16px;
          padding: 14px 24px;
          background: ${({ theme }) => theme.cardBg};
          border-bottom: 1px solid ${({ theme }) => theme.border};

          .header-cell {
            font-size: 0.75em;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        }

        .table-body {
          .table-row {
            display: grid;
            grid-template-columns: 1.5fr 0.8fr 1.2fr 1.2fr 1.2fr 1fr;
            gap: 16px;
            padding: 16px 24px;
            border-bottom: 1px solid ${({ theme }) => theme.border};
            transition: background 0.2s ease;

            &:hover {
              background: ${({ theme }) => theme.tableRowHover};
            }

            &:last-child {
              border-bottom: none;
            }

            .cell {
              font-size: 0.85em;
              color: ${({ theme }) => theme.text};
              display: flex;
              align-items: center;

              &.segmento {
                font-weight: 600;
              }

              .percent-bar {
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;

                .percent-fill {
                  height: 6px;
                  background: ${({ theme }) => theme.primary};
                  border-radius: 3px;
                  flex: 1;
                  max-width: 60%;
                }

                span {
                  font-size: 0.85em;
                  font-weight: 600;
                  color: ${({ theme }) => theme.text};
                }
              }
            }
          }
        }
      }
    }

    /* Insights e Alertas */
    .insights-row {
      display: grid;
      grid-template-columns: 1.5fr 1fr;
      gap: 20px;

      .insights-card,
      .alerts-card {
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 16px;
        padding: 20px;
      }

      .insights-header,
      .alerts-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid ${({ theme }) => theme.border};

        svg {
          font-size: 1.2em;
          color: ${({ theme }) => theme.primary};
        }

        h4 {
          margin: 0;
          font-size: 0.95em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }

      .insights-list {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .insight-item {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px;
          border-radius: 12px;
          border: 1px solid ${({ theme }) => theme.border};

          &.positive {
            background: rgba(16, 185, 129, 0.05);
            border-color: rgba(16, 185, 129, 0.2);

            .insight-icon {
              background: rgba(16, 185, 129, 0.15);
              color: #10b981;
            }
          }

          &.warning {
            background: rgba(245, 158, 11, 0.05);
            border-color: rgba(245, 158, 11, 0.2);

            .insight-icon {
              background: rgba(245, 158, 11, 0.15);
              color: #f59e0b;
            }
          }

          &.info {
            background: rgba(59, 130, 246, 0.05);
            border-color: rgba(59, 130, 246, 0.2);

            .insight-icon {
              background: rgba(59, 130, 246, 0.15);
              color: #3b82f6;
            }
          }

          .insight-icon {
            width: 36px;
            height: 36px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1em;
            flex-shrink: 0;
          }

          .insight-content {
            flex: 1;

            .insight-title {
              display: block;
              font-size: 0.85em;
              font-weight: 600;
              color: ${({ theme }) => theme.text};
              margin-bottom: 4px;
            }

            .insight-desc {
              display: block;
              font-size: 0.75em;
              color: ${({ theme }) => theme.text};
              opacity: 0.6;
            }
          }
        }
      }

      .alerts-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;

        .alert-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 20px;
          background: ${({ theme }) => theme.buttonBg};
          border-radius: 12px;
          text-align: center;

          .alert-value {
            font-size: 1.8em;
            font-weight: 700;
            color: ${({ theme }) => theme.text};
          }

          .alert-label {
            font-size: 0.75em;
            color: ${({ theme }) => theme.text};
            opacity: 0.6;
            margin-top: 4px;
          }
        }
      }
    }
  }

  /* ========== REFORMA TRIBUTÁRIA TAB ========== */
  .reforma-tab {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0;
  }

  .cenario-buttons {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;

    .cenario-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 14px 28px;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.border};
      border-left: 4px solid transparent;
      border-radius: 12px;
      cursor: pointer;
      transition: all 0.25s ease;

      &:hover {
        border-left-color: #3b82f6;
        background: rgba(59, 130, 246, 0.05);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
      }

      &.active {
        border-left-color: #3b82f6;
        background: rgba(59, 130, 246, 0.08);
        box-shadow: 0 4px 16px rgba(59, 130, 246, 0.15);

        .cenario-year {
          color: #3b82f6;
        }

        .cenario-label {
          opacity: 0.8;
        }
      }

      .cenario-year {
        font-size: 1.2em;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
        font-family: 'Inter', system-ui, sans-serif;
        letter-spacing: -0.02em;
      }

      .cenario-label {
        font-size: 0.8em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        font-weight: 500;
      }
    }

    @media (max-width: 768px) {
      flex-wrap: wrap;

      .cenario-btn {
        flex: 1;
        min-width: 120px;
        padding: 12px 20px;
      }
    }
  }

  .reforma-search {
    margin-top: 8px;
    margin-bottom: 12px;

    .search-input-wrapper {
      position: relative;
      width: 100%;

      .search-icon {
        position: absolute;
        left: 16px;
        top: 50%;
        transform: translateY(-50%);
        color: ${({ theme }) => theme.text};
        opacity: 0.4;
        font-size: 1.1em;
      }

      .search-input {
        width: 100%;
        padding: 14px 16px 14px 48px;
        background: ${({ theme }) => theme.card};
        border: 1px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        font-size: 0.9em;
        color: ${({ theme }) => theme.text};
        transition: all 0.2s ease;

        &::placeholder {
          color: ${({ theme }) => theme.text};
          opacity: 0.4;
        }

        &:focus {
          outline: none;
          border-color: ${({ theme }) => theme.primary};
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }
      }
    }
  }

  .reforma-content-row {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 16px;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  .reforma-products-table {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    overflow: hidden;

    .table-header {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
      gap: 12px;
      padding: 14px 20px;
      background: ${({ theme }) => theme.cardBg};
      border-bottom: 1px solid ${({ theme }) => theme.border};

      .header-cell {
        font-size: 0.75em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        text-align: center;

        &.produto {
          text-align: left;
        }
      }
    }

    .table-body {
      max-height: 600px;
      overflow-y: overlay;

      &::-webkit-scrollbar {
        width: 4px;
      }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.border};
        border-radius: 2px;
      }
    }

    .product-row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 1fr;
      gap: 12px;
      padding: 14px 20px;
      border-bottom: 1px solid ${({ theme }) => theme.border};
      border-left: 3px solid var(--product-color, #64748b);
      transition: background 0.2s ease;
      cursor: pointer;

      &:hover {
        background: ${({ theme }) => theme.tableRowHover};
      }

      &:last-child {
        border-bottom: none;
      }

      .product-info {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .product-code {
          font-size: 0.75em;
          color: var(--product-color, #64748b);
          font-family: "SF Mono", "Consolas", "Monaco", monospace;
          font-weight: 600;
        }

        .product-name {
          font-size: 0.85em;
          font-weight: 500;
          color: ${({ theme }) => theme.text};
          opacity: 0.9;
          line-height: 1.3;
        }
      }

      .tax-value {
        font-size: 0.85em;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
        opacity: 0.8;
        text-align: center;
        font-family: 'Inter', system-ui, sans-serif;
      }
    }
  }

  .impacto-sidebar {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .impacto-main-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-left: 4px solid #ef4444;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;

    .impacto-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
    }

    .impacto-icon {
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(239, 68, 68, 0.1);
      border-radius: 12px;
      color: #ef4444;
      font-size: 1.1em;
    }

    .impacto-badge {
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 0.65em;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;

      &.alto {
        background: rgba(239, 68, 68, 0.15);
        color: #ef4444;
      }

      &.medio {
        background: rgba(245, 158, 11, 0.15);
        color: #f59e0b;
      }

      &.baixo {
        background: rgba(16, 185, 129, 0.15);
        color: #10b981;
      }
    }

    .impacto-title {
      font-size: 0.8em;
      color: ${({ theme }) => theme.text};
      opacity: 0.6;
    }

    .impacto-value {
      font-size: 1.3em;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
      font-family: 'Inter', system-ui, sans-serif;
      letter-spacing: -0.02em;
    }

    .impacto-desc {
      font-size: 0.75em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }

    .impacto-variation {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-top: 6px;
      padding-top: 10px;
      border-top: 1px solid ${({ theme }) => theme.border};

      svg {
        color: #ef4444;
        font-size: 1em;
      }

      span {
        font-size: 0.9em;
        font-weight: 700;
        color: #ef4444;
      }

      .variation-label {
        font-size: 0.75em;
        font-weight: 400;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }
    }
  }

  .impacto-compare-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 14px;

    .compare-title {
      font-size: 0.75em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      display: block;
      margin-bottom: 10px;
    }

    .compare-items {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .compare-item {
      display: flex;
      justify-content: space-between;
      align-items: center;

      &.highlight {
        padding-top: 10px;
        margin-top: 4px;
        border-top: 1px solid ${({ theme }) => theme.border};
      }

      .compare-label {
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
      }

      .compare-value {
        font-size: 0.85em;
        font-weight: 600;
        font-family: 'Inter', system-ui, sans-serif;

        &.atual {
          color: ${({ theme }) => theme.text};
        }

        &.novo {
          color: #3b82f6;
        }

        &.diff {
          color: #ef4444;
        }
      }
    }
  }

  .impacto-aliquotas-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 14px;

    .aliquotas-title {
      font-size: 0.75em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      display: block;
      margin-bottom: 10px;
    }

    .aliquota-row {
      display: flex;
      gap: 10px;
      margin-bottom: 10px;
    }

    .aliquota-item {
      flex: 1;
      background: ${({ theme }) => theme.buttonBg};
      border-radius: 12px;
      padding: 10px;
      text-align: center;

      .aliquota-sigla {
        display: block;
        font-size: 0.7em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        margin-bottom: 4px;
      }

      .aliquota-valor {
        font-size: 1.1em;
        font-weight: 700;
        color: #3b82f6;
        font-family: 'Inter', system-ui, sans-serif;
      }
    }

    .aliquota-total {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-top: 12px;
      border-top: 1px solid ${({ theme }) => theme.border};

      span {
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
      }

      .total-valor {
        font-size: 1em;
        font-weight: 700;
        color: ${({ theme }) => theme.primary};
        opacity: 1;
        font-family: 'Inter', system-ui, sans-serif;
      }
    }
  }

  .impacto-timeline-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 14px;

    .timeline-title {
      font-size: 0.75em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      opacity: 0.7;
      display: block;
      margin-bottom: 12px;
    }

    .timeline-steps {
      display: flex;
      justify-content: space-between;
      gap: 8px;
    }

    .timeline-step {
      flex: 1;
      text-align: center;
      padding: 10px 8px;
      background: ${({ theme }) => theme.buttonBg};
      border-radius: 12px;
      border: 1px solid transparent;
      transition: all 0.2s ease;

      &.active {
        border-color: #3b82f6;
        background: rgba(59, 130, 246, 0.1);

        .step-year {
          color: #3b82f6;
        }
      }

      .step-year {
        display: block;
        font-size: 0.9em;
        font-weight: 700;
        color: ${({ theme }) => theme.text};
        margin-bottom: 2px;
      }

      .step-desc {
        font-size: 0.65em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }
    }
  }

  .reforma-charts-row {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 16px;

    .reforma-chart-card {
      flex: 1;
    }

    @media (max-width: 1200px) {
      flex-direction: column;
    }
  }

  .reforma-chart-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;

    .chart-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 8px;

      .chart-title {
        display: flex;
        align-items: center;
        gap: 10px;

        .chart-icon {
          font-size: 1.2em;
          color: #3b82f6;
        }

        span {
          font-size: 0.95em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
        }
      }

      .chart-subtitle {
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
      }
    }

    .chart-container {
      flex: 1;
      min-height: auto;
      padding: 0;
    }
  }

  /* Tabela de Projeção de Débitos/Créditos - Padrão vidal-ravena */
  .projecao-debitos-section {
    margin-top: 24px;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 16px;
    overflow: hidden;

    .section-header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      border-bottom: 1px solid ${({ theme }) => theme.border};
      background: ${({ theme }) => theme.cardBg};

      .section-title {
        display: flex;
        align-items: center;
        gap: 12px;

        .section-icon {
          font-size: 1.4em;
          color: ${({ theme }) => theme.primary};
        }

        h3 {
          font-size: 1.1em;
          font-weight: 600;
          color: ${({ theme }) => theme.text};
          margin: 0;
        }
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: 16px;

        .table-search-input {
          position: relative;
          display: flex;
          align-items: center;

          .search-icon {
            position: absolute;
            left: 12px;
            font-size: 1.1em;
            color: ${({ theme }) => theme.primary};
            opacity: 0.8;
          }

          input {
            padding: 8px 16px 8px 40px;
            border: 2px solid ${({ theme }) => theme.border};
            border-radius: 12px;
            background: ${({ theme }) => theme.background};
            color: ${({ theme }) => theme.text};
            font-size: 0.9em;
            font-weight: 500;
            width: 300px;
            transition: all 0.2s ease;

            &::placeholder {
              color: ${({ theme }) => theme.text};
              opacity: 0.5;
            }

            &:hover {
              border-color: ${({ theme }) => theme.primary};
            }

            &:focus {
              outline: none;
              border-color: ${({ theme }) => theme.primary};
              box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.15);
            }
          }
        }

        .products-count {
          font-size: 0.85em;
          color: ${({ theme }) => theme.text};
          opacity: 0.5;
          background: ${({ theme }) => theme.buttonBg};
          padding: 6px 12px;
          border-radius: 20px;
        }
      }
    }

    .projecao-table-container {
      overflow-x: auto;

      &::-webkit-scrollbar {
        height: 6px;
      }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => theme.border};
        border-radius: 3px;
      }
    }

    .projecao-table {
      width: 100%;
      border-collapse: collapse;
      min-width: 1100px;

      thead {
        tr:first-child {
          th {
            padding: 16px 12px 8px;
            font-size: 0.7em;
            font-weight: 600;
            color: ${({ theme }) => theme.text};
            opacity: 0.5;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            text-align: left;
            border-bottom: none;

            .header-group {
              display: inline-block;
              padding: 6px 14px;
              border-radius: 6px;
              background: ${({ theme }) => theme.buttonBg};
              font-size: 1.1em;

              &.cbs {
                background: rgba(59, 130, 246, 0.1);
                color: #3b82f6;
              }

              &.variacao {
                background: rgba(139, 92, 246, 0.1);
                color: #8b5cf6;
              }

              &.creditos-atuais {
                background: rgba(16, 185, 129, 0.1);
                color: #10b981;
              }

              &.variacao-creditos {
                background: rgba(59, 130, 246, 0.1);
                color: #3b82f6;
              }
            }
          }
        }

        tr.sub-header {
          th {
            padding: 8px 12px 12px;
            font-size: 0.7em;
            font-weight: 500;
            color: ${({ theme }) => theme.text};
            opacity: 0.4;
            text-align: left;
            border-bottom: 1px solid ${({ theme }) => theme.border};

            &.sub-col {
              text-align: center;
            }
          }
        }
      }

      tbody {
        tr.data-row {
          transition: all 0.2s ease;
          border-left: 3px solid transparent;

          &:hover {
            background: ${({ theme }) => theme.tableRowHover};
          }

          &.reducao {
            border-left-color: #10b981;
          }

          &.aumento {
            border-left-color: #ef4444;
          }

          &.neutro {
            border-left-color: #f59e0b;
          }

          &.ganho {
            border-left-color: #10b981;
          }

          &.perda {
            border-left-color: #ef4444;
          }

          td {
            padding: 14px 12px;
            font-size: 0.85em;
            color: ${({ theme }) => theme.text};
            border-bottom: 1px solid ${({ theme }) => theme.border};
            vertical-align: middle;

            &.col-ranking {
              .ranking-badge {
                font-family: "SF Mono", "Consolas", monospace;
                font-size: 0.85em;
                font-weight: 600;
                color: ${({ theme }) => theme.primary};
                background: rgba(16, 185, 129, 0.1);
                padding: 4px 10px;
                border-radius: 6px;

                &.credito {
                  color: #3b82f6;
                  background: rgba(59, 130, 246, 0.1);
                }
              }
            }

            &.col-cliente {
              .cliente-nome {
                font-weight: 500;
                color: ${({ theme }) => theme.text};
                opacity: 0.9;
                max-width: 200px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                display: block;
              }
            }

            &.col-cnpj {
              .cnpj-text {
                font-family: "SF Mono", "Consolas", monospace;
                font-size: 0.9em;
                color: ${({ theme }) => theme.text};
                opacity: 0.7;
              }
            }

            &.col-valor {
              .valor-principal {
                font-weight: 600;
                font-family: 'Inter', system-ui, sans-serif;
                color: ${({ theme }) => theme.text};
              }
            }

            &.col-debito,
            &.col-cbs {
              text-align: center;
              font-family: 'Inter', system-ui, sans-serif;
              opacity: 0.8;

              &.total {
                font-weight: 600;
                opacity: 1;
                color: #3b82f6;
              }
            }

            &.col-credito-atual {
              text-align: center;
              font-family: 'Inter', system-ui, sans-serif;
              opacity: 0.8;

              &.total {
                font-weight: 600;
                opacity: 1;
                color: #10b981;
              }
            }

            &.col-aliq {
              text-align: center;
              font-weight: 600;
              color: #8b5cf6;
            }

            &.col-diferenca {
              text-align: center;
              font-weight: 700;
              font-family: 'Inter', system-ui, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 6px;

              &.reducao {
                color: #10b981;
              }

              &.aumento {
                color: #ef4444;
              }

              &.neutro {
                color: #f59e0b;
              }
            }

            &.col-ganho-perda {
              text-align: center;
              font-weight: 700;
              font-family: 'Inter', system-ui, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 6px;

              &.ganho {
                color: #10b981;
              }

              &.perda {
                color: #ef4444;
              }

              &.neutro {
                color: #f59e0b;
              }
            }

            .impacto-badge {
              display: inline-block;
              padding: 4px 10px;
              border-radius: 20px;
              font-size: 0.7em;
              font-weight: 600;
              text-transform: uppercase;
              letter-spacing: 0.3px;

              &.reducao {
                background: rgba(16, 185, 129, 0.15);
                color: #10b981;
              }

              &.aumento {
                background: rgba(239, 68, 68, 0.15);
                color: #ef4444;
              }

              &.neutro {
                background: rgba(245, 158, 11, 0.15);
                color: #f59e0b;
              }

              &.ganho {
                background: rgba(16, 185, 129, 0.15);
                color: #10b981;
              }

              &.perda {
                background: rgba(239, 68, 68, 0.15);
                color: #ef4444;
              }

              &.alto {
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
              }
            }
          }
        }
      }
    }

    .table-legend {
      display: flex;
      justify-content: center;
      gap: 20px;
      padding: 16px 24px;
      background: ${({ theme }) => theme.cardBg};
      border-top: 1px solid ${({ theme }) => theme.border};
      flex-wrap: wrap;

      .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 0.75em;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;

        .legend-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        &.reducao .legend-dot {
          background: #10b981;
        }

        &.reducao-leve .legend-dot {
          background: #34d399;
        }

        &.neutro .legend-dot {
          background: #f59e0b;
        }

        &.aumento-leve .legend-dot {
          background: #f87171;
        }

        &.aumento .legend-dot {
          background: #ef4444;
        }

        &.ganho-alto .legend-dot {
          background: #10b981;
        }

        &.ganho .legend-dot {
          background: #34d399;
        }

        &.perda .legend-dot {
          background: #f87171;
        }

        &.perda-alta .legend-dot {
          background: #ef4444;
        }
      }
    }

    &.creditos {
      .section-title .section-icon {
        color: #3b82f6;
      }
    }
  }
`;

export const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
  align-items: start;

  .clients-table {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow: hidden;

    .table-header {
      display: grid;
      grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr);
      gap: 16px;
      padding: 10px 16px;
      background: ${({ theme }) => surface(theme, 1)};
      border-bottom: 1px solid ${({ theme }) => hairline(theme)};

      .header-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 5px;
        font-size: 0.68em;
        font-weight: 600;
        color: ${({ theme }) => theme.text};
        opacity: 0.5;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        white-space: nowrap;
        ${mono};

        svg { font-size: 0.95em; opacity: 0.4; }

        &.cliente { justify-content: flex-start; }
      }
    }

    .table-body {
      display: flex;
      flex-direction: column;
      max-height: 600px;
      overflow-y: auto;

      &::-webkit-scrollbar { width: 4px; }
      &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => hairline(theme, true)};
        border-radius: 2px;
      }
    }

  }

  .segments-card {
    height: 650px;
    position: sticky;
    top: 20px;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;

    .segments-card {
      position: static;
      height: 500px;
    }
  }
`;
